import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { OverviewSettingRow } from "@/components/agent/overview/OverviewSettingRow";
import { ProviderIcon } from "@/components/agent/core/ProviderIcon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { useLlmModels } from "@/hooks/voice/useLlmModels";
import { Agent } from "@/types/agent/agent";

type BackupPreference = "default" | "disabled" | "override";

const DEFAULT_LLM = "gpt-5.6-luna";
const DEFAULT_BACKUP_LLM = "gpt-5-nano";

interface ModelIntelligenceCardProps {
  agent: Agent;
  brand?: string;
}

function toFriendlySaveErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("file_too_large") || message.toLowerCase().includes("knowledge base is too large")) {
    return "Your knowledge base is too large for the selected model. Please remove some files or choose a model with a larger context window.";
  }
  return "Failed to save model settings. Please try again.";
}

export const ModelIntelligenceCard = ({ agent, brand = "#4E9CFF" }: ModelIntelligenceCardProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const prompt = agent.settings?.conversation_config?.agent?.prompt;
  const [llm, setLlm] = useState(prompt?.llm ?? DEFAULT_LLM);
  const [temperature, setTemperature] = useState(prompt?.temperature ?? 0.7);
  const [maxTokens, setMaxTokens] = useState(prompt?.max_tokens ?? "");
  const [ragEnabled, setRagEnabled] = useState(prompt?.rag?.enabled ?? false);
  const [backupPreference, setBackupPreference] = useState<BackupPreference>(
    prompt?.backup_llm_config?.preference ?? "default",
  );
  const [backupOrder, setBackupOrder] = useState<Array<string>>(
    prompt?.backup_llm_config?.preference === "override" ? prompt.backup_llm_config.order : [],
  );

  const updateAgent = useUpdateAgent();
  const { data: llmModels = [] } = useLlmModels();

  const handleEdit = () => {
    setLlm(prompt?.llm ?? DEFAULT_LLM);
    setTemperature(prompt?.temperature ?? 0.7);
    setMaxTokens(prompt?.max_tokens ?? "");
    setRagEnabled(prompt?.rag?.enabled ?? false);
    setBackupPreference(prompt?.backup_llm_config?.preference ?? "default");
    setBackupOrder(prompt?.backup_llm_config?.preference === "override" ? prompt.backup_llm_config.order : []);
    setIsEditing(true);
  };

  const addBackupModel = (modelId: string) => {
    if (!modelId || backupOrder.includes(modelId)) return;
    setBackupOrder((prev) => [...prev, modelId]);
  };

  const removeBackupModel = (modelId: string) => {
    setBackupOrder((prev) => prev.filter((id) => id !== modelId));
  };

  const moveBackupModel = (index: number, direction: -1 | 1) => {
    setBackupOrder((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    const backupLlmConfig =
      backupPreference === "override"
        ? { preference: "override" as const, order: backupOrder }
        : { preference: backupPreference as "default" | "disabled" };

    try {
      await updateAgent.mutateAsync({
        agent,
        updateData: {
          settings: {
            ...agent.settings,
            conversation_config: {
              ...agent.settings?.conversation_config,
              agent: {
                ...agent.settings?.conversation_config?.agent,
                prompt: {
                  ...prompt,
                  llm: llm || undefined,
                  temperature: Number(temperature),
                  max_tokens: maxTokens !== "" ? Number(maxTokens) : undefined,
                  rag: { enabled: ragEnabled },
                  backup_llm_config: backupLlmConfig,
                },
              },
            },
          },
        },
      });
      toast.success("Model settings saved");
      setIsEditing(false);
    } catch (error) {
      toast.error(toFriendlySaveErrorMessage(error));
    }
  };

  const getTemperatureDescription = (temp: number) => {
    if (temp < 0.3) return "Very Precise";
    if (temp < 0.7) return "Balanced";
    if (temp < 1.2) return "Creative";
    return "Very Creative";
  };

  const currentTemperature = prompt?.temperature ?? 0.7;
  const currentModel = prompt?.llm ?? "gpt-4o";
  const currentModelProvider = llmModels.find((m) => m.id === currentModel)?.provider;
  const currentMaxTokens = prompt?.max_tokens;
  const currentRagEnabled = prompt?.rag?.enabled ?? false;

  return (
    <OverviewSectionCard
      title="Model & Intelligence"
      subtitle="AI model configuration and response behavior"
      genieIcon="ai-chip"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LLM Model</label>
            <Select value={llm} onValueChange={setLlm}>
              <SelectTrigger className="w-full rounded-md border-gray-300 px-3 py-2 text-sm h-auto">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {llmModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <span className="flex items-center gap-2">
                      <ProviderIcon provider={model.provider} />
                      {model.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature: {temperature}</label>
            <div className="relative mt-2 mb-1">
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
                  style={{ width: `${(temperature / 2) * 100}%` }}
                />
              </div>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                className="absolute inset-0 w-full opacity-0 cursor-pointer h-2"
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Precise</span>
              <span>{getTemperatureDescription(Number(temperature))}</span>
              <span>Creative</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
            <input
              type="number"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. 1000"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <label className="text-sm font-medium text-gray-700">Knowledge Retrieval (RAG)</label>
            <Switch checked={ragEnabled} onCheckedChange={setRagEnabled} />
          </div>
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Backup LLM Cascading</label>
                <p className="text-xs text-gray-400 mt-0.5">
                  When disabled, turns fail instead of silently falling back to another model.
                </p>
              </div>
              <Switch
                checked={backupPreference !== "disabled"}
                onCheckedChange={(checked) => setBackupPreference(checked ? "default" : "disabled")}
              />
            </div>

            {backupPreference !== "disabled" && (
              <div className="pt-2 border-t border-gray-200 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700">Custom Fallback Order</label>
                  <Switch
                    checked={backupPreference === "override"}
                    onCheckedChange={(checked) => {
                      setBackupPreference(checked ? "override" : "default");
                      if (checked && backupOrder.length === 0) {
                        setBackupOrder([DEFAULT_BACKUP_LLM]);
                      }
                    }}
                  />
                </div>

                {backupPreference === "override" && (
                  <>
                    <p className="text-xs text-gray-400">
                      Models are tried top to bottom. Leave empty to use the platform default order.
                    </p>
                    <Select value="" onValueChange={addBackupModel}>
                      <SelectTrigger className="w-full rounded-md border-gray-300 px-3 py-2 text-sm h-auto">
                        <SelectValue placeholder="Add a fallback model..." />
                      </SelectTrigger>
                      <SelectContent>
                        {llmModels
                          .filter((model) => !backupOrder.includes(model.id))
                          .map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              <span className="flex items-center gap-2">
                                <ProviderIcon provider={model.provider} />
                                {model.name}
                              </span>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    {backupOrder.length > 0 && (
                      <ul className="space-y-1">
                        {backupOrder.map((modelId, index) => {
                          const model = llmModels.find((m) => m.id === modelId);
                          return (
                            <li
                              key={modelId}
                              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm"
                            >
                              <span className="text-gray-500 w-4 text-xs">{index + 1}.</span>
                              <span className="flex-1 truncate">{model?.name ?? modelId}</span>
                              <button
                                type="button"
                                onClick={() => moveBackupModel(index, -1)}
                                disabled={index === 0}
                                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              >
                                <ArrowUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveBackupModel(index, 1)}
                                disabled={index === backupOrder.length - 1}
                                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                              >
                                <ArrowDown className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeBackupModel(modelId)}
                                className="text-gray-400 hover:text-red-600"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Model"
            value={
              <span className="font-medium flex items-center gap-1.5">
                {currentModelProvider && <ProviderIcon provider={currentModelProvider} />}
                {currentModel}
              </span>
            }
          />
          <OverviewSettingRow
            label="Temperature"
            value={
              <div className="flex items-center gap-3">
                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
                    style={{ width: `${(currentTemperature / 2) * 100}%` }}
                  />
                </div>
                <span className="font-medium text-xs">
                  {currentTemperature} ({getTemperatureDescription(currentTemperature)})
                </span>
              </div>
            }
          />
          {currentMaxTokens != null && (
            <OverviewSettingRow
              label="Max Tokens"
              value={<span className="font-medium">{currentMaxTokens.toLocaleString()}</span>}
            />
          )}
          <OverviewSettingRow
            label="Knowledge Retrieval (RAG)"
            value={<span className="font-medium">{currentRagEnabled ? "Enabled" : "Disabled"}</span>}
          />
          <OverviewSettingRow
            label="Backup LLM Cascading"
            value={
              <span className="font-medium capitalize">{prompt?.backup_llm_config?.preference ?? "default"}</span>
            }
          />
          {prompt?.backup_llm_config?.preference === "override" && prompt.backup_llm_config.order.length > 0 && (
            <OverviewSettingRow
              label="Fallback Order"
              value={
                <span className="font-medium text-right">
                  {prompt.backup_llm_config.order
                    .map((id) => llmModels.find((m) => m.id === id)?.name ?? id)
                    .join(" → ")}
                </span>
              }
            />
          )}
          <div className="mt-4 p-3 rounded-lg border border-[#4E9CFF30] bg-gradient-to-r from-[#4E9CFF08] to-[#FF4DB808]">
            <p className="text-xs bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
              <strong>Temperature:</strong> Lower values make responses more focused. Higher values make them more
              creative.
            </p>
          </div>
        </div>
      )}
    </OverviewSectionCard>
  );
};
